import { apiCall } from '../../../Middleware/API/apiRequest';
import { getTokensFromStorage } from '../../../Utils/Auth/AuthToken';
import { objectToQueryString } from 'smart-react';

const { accessToken } = getTokensFromStorage();

/**
 * Lists Rollout.
 * @returns {Promise} - A promise that resolves with the list of Rollout or rejects with an error.
 * @param props
 */
export const getRolloutList = async ({
  limit,
  offset,
  filter,
  tags,
  sort,
  signal,
  moduleName,
  isServiceWorker,
  listController,
  projectId,
  versionId,
}) => {
  const data = {
    Filters: Array.isArray(filter) ? filter : [],
    tags: Array.isArray(tags) ? tags : [],
    pageNumber: offset === 0 ? 1 : offset,
    pageSize: String(limit),
    sort: Array.isArray(sort) ? sort : [],
  };
  const response = await apiRequest({
    url: `/rollouts/filter/${projectId}/${versionId}`,
    method: 'POST',
    signal: signal ?? listController?.signal,
    moduleName,
    isServiceWorker,
    listController,
    body: JSON.stringify(data),
    accessToken,
  });
  if (!response?.isSuccess) {
    if (isServiceWorker) {
      let cacheResponse = await getGraphqlCacheResponse(
        headers,
        isServiceWorker
      );
      return cacheResponse;
    }
    return response;
  }
  if (isServiceWorker) {
    await setGraphqlCacheResponse(headers, response, isServiceWorker);
  }
  return {
    isSuccess: response?.isSuccess,
    rollouts: response?.payload,
    TotalCount: response?.totalRecordCount,
  };
};

/**
 * Lists Bugs Data.
 * @returns {Promise} - A promise that resolves with the list of Bugs or rejects with an error.
 * @param props
 */
export const getBugData = async ({
  limit,
  offset,
  filter,
  sort,
  moduleName,
  isServiceWorker,
  listController,
  rolloutName,
}) => {
  const data = {
    Filters: filter,
    pageNumber: offset === 0 ? 1 : offset,
    pageSize: limit,
    sort,
  };
  const response = await apiRequest({
    url: `/rollouts/${rolloutName}/issues`,
    method: 'POST',
    moduleName,
    isServiceWorker,
    listController,
    body: JSON.stringify(data),
    accessToken,
  });
  if (!response?.isSuccess) {
    if (isServiceWorker) {
      let cacheResponse = await getGraphqlCacheResponse(
        headers,
        isServiceWorker
      );
      return cacheResponse;
    }
    return response;
  }
  if (isServiceWorker) {
    await setGraphqlCacheResponse(headers, response, isServiceWorker);
  }

  return {
    isSuccess: response?.isSuccess,
    bugsData: response?.payload,
    TotalCount: response?.totalRecordCount,
  };
};

/**
 * delete Rollout.
 * @returns {Promise} - A promise that resolves with the download Rollout Archives or rejects with an error.
 * @param props
 */
export const deleteRolloutData = async (rolloutTempName) => {
  const response = await apiRequest({
    url: `/rollouts/${rolloutTempName}`,
    method: 'DELETE',
    accessToken,
  });
  return response;
};

/**
 * download Bug.
 * @returns {Promise} - A promise that resolves with the download Bug Archives or rejects with an error.
 * @param props
 */
export const downloadRolloutBug = async (data) => {
  let response = await apiRequest({
    url: `/rollouts/download`,
    method: 'POST',
    body: JSON.stringify(data),
    accessToken,
  });
  return response;
};

const normalizeRolloutDownloadResponse = (response) => {
  if (!response?.isSuccess) {
    return {
      isSuccess: false,
      message: response?.message || 'Unable to download rollout archive.',
      payload: response?.payload,
    };
  }

  const downloadUrl = String(response?.payload?.downloadUrl ?? '').trim();

  if (!downloadUrl) {
    return {
      isSuccess: false,
      message:
        response?.message || 'Download URL was not returned by the server.',
      payload: response?.payload,
    };
  }

  return {
    ...response,
    payload: {
      ...response?.payload,
      downloadUrl,
    },
  };
};

const buildLegacyRolloutDownloadQuery = (options = {}) =>
  objectToQueryString({
    ...(String(options?.versionId ?? '').trim()
      ? { versionId: String(options.versionId).trim() }
      : {}),
    ...(String(options?.environment ?? '').trim()
      ? { environment: String(options.environment).trim() }
      : {}),
    ...(String(options?.majorVersion ?? '').trim()
      ? { majorVersion: String(options.majorVersion).trim() }
      : {}),
    ...(String(options?.minorVersion ?? '').trim()
      ? { minorVersion: String(options.minorVersion).trim() }
      : {}),
  });

/**
 * download Rollout Archives for the default configured version.
 * @returns {Promise} - A promise that resolves with the download Rollout Archives or rejects with an error.
 * @param props
 */
export const downloadRolloutArchiveDefault = async (
  platformType,
  rolloutNameState
) => {
  const normalizedPlatformType = String(platformType ?? '').trim();
  const normalizedRolloutName = String(rolloutNameState ?? '').trim();
  const queryParameters = objectToQueryString({
    ...(normalizedPlatformType ? { platform: normalizedPlatformType } : {}),
  });
  const response = await apiRequest({
    url: `/rollouts/${encodeURIComponent(
      normalizedRolloutName
    )}/download${queryParameters ? `?${queryParameters}` : ''}`,
    method: 'GET',
    accessToken,
  });

  return normalizeRolloutDownloadResponse(response);
};

/**
 * download Rollout Archives for a specific configured environment.
 * @returns {Promise} - A promise that resolves with the download Rollout Archives or rejects with an error.
 * @param props
 */
export const downloadRolloutArchiveByEnvironment = async (
  platformType,
  rolloutNameState,
  environmentState
) => {
  const normalizedPlatformType = String(platformType ?? '').trim();
  const normalizedRolloutName = String(rolloutNameState ?? '').trim();
  const normalizedEnvironment = String(environmentState ?? '').trim();
  const queryParameters = objectToQueryString({
    ...(normalizedPlatformType ? { platform: normalizedPlatformType } : {}),
  });
  const response = await apiRequest({
    url: `/rollouts/${encodeURIComponent(
      normalizedRolloutName
    )}/environments/${encodeURIComponent(normalizedEnvironment)}/download${
      queryParameters ? `?${queryParameters}` : ''
    }`,
    method: 'GET',
    accessToken,
  });

  return normalizeRolloutDownloadResponse(response);
};

/**
 * download Rollout Archives using the legacy endpoint.
 * @returns {Promise} - A promise that resolves with the download Rollout Archives or rejects with an error.
 * @param props
 */
export const downloadRolloutArchives = async (
  platformType,
  rolloutNameState,
  options = {}
) => {
  const normalizedPlatformType = String(platformType ?? '').trim();
  const normalizedRolloutName = String(rolloutNameState ?? '').trim();
  const queryParameters = buildLegacyRolloutDownloadQuery(options);
  const response = await apiRequest({
    url: `/rollouts/download/${encodeURIComponent(
      normalizedPlatformType
    )}/${encodeURIComponent(normalizedRolloutName)}${
      queryParameters ? `?${queryParameters}` : ''
    }`,
    method: 'GET',
    accessToken,
  });

  return normalizeRolloutDownloadResponse(response);
};

/**
 * Api Call Wrapper Method.
 * @param {object} api - Contains details like url headers method and data.
 * @returns {Promise} - A promise that resolves with the data or rejects with an error.
 */
const apiRequest = async (request) => {
  let response;
  const headers = {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  };
  request.headers = headers;
  try {
    response = await apiCall(request);

    if (!response.isSuccess) {
      return { isSuccess: false, message: response.message };
    }
    return response;
  } catch (e) {
    response = { isSuccess: false, message: e.message };
  }
  return response;
};